// Percorso file: src/pages/EditMemberPage.jsx
//
// LA MODIFICA DELLA SCHEDA DEL TESSERATO — PAGINA, non piu' finestra
// sovrapposta (cantiere «Edit Member»; `docs/cantieri/EDIT-MEMBER-SPEC.md`
// §1, doc 07 §12).
//
// La fase 1 ha spostato il modulo (modale → pagina); la fase 2 lo ha VESTITO
// sui token e ristrutturato in sezioni-card. Il contenuto — campi,
// validazioni, payload, salvataggio — resta quello di
// `components/EditMemberModal.jsx`, che questa pagina monta: la modale non
// esiste piu', quindi il componente rende sempre e solo il proprio contenuto
// e il telaio lo mette questa pagina (la prop-ponte `asPage` della fase 1 e'
// caduta con il guscio che disattivava).
//
// La pagina e' ricalcata su View Profile (`components/MemberProfileModal.jsx`,
// fase 3), e di proposito NON inventa nulla di suo:
//   1. l'id arriva dalla ROTTA (`useParams`), non da una prop;
//   2. l'uscita e' il «Back» della banda, con la stessa guardia sulla
//      cronologia;
//   3. il rifiuto si mostra DENTRO il telaio, non come pagina rotta;
//   4. il documento si legge dalla porta unica, `GET /users/:id`.
//
// ⚠️ ACCESSO SOLO ADMIN/SUPERADMIN, CONTROLLATO ALLA ROTTA. Da quando il
// modulo ha un indirizzo, chiunque puo' digitarlo: il gate non puo' piu'
// stare sul solo pulsante che lo apriva. Il metro e' quello gia' usato dalla
// scheda per mostrare «Edit Member» (`hasRole`), non un cancello nuovo.
// Resta cortesia grafica: la protezione vera e' sul backend, che rifiuta con
// 403 il `PUT /users/:id` di chi non ha titolo.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HomeShell from "../components/HomeShell";
import EditMemberModal from "../components/EditMemberModal";
import { api } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../utils/roleUtils";

const EditMemberPage = () => {
  const { user: utenteCollegato, refreshUserFromBackend } = useAuth() || {};

  const { id: idProfilo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Chi puo' stare qui. Stesso metro del pulsante «Edit Member» della scheda
  // (`MemberProfileModal.jsx`, `chiamanteEAdmin`): un solo vocabolario di
  // ruoli per la stessa azione.
  // ⚠️ Non serve attendere l'autenticazione: la rotta e' avvolta da
  // `PrivateRoute` (cioe' `RequireRole` senza ruolo), che non rende nulla
  // finche' `isReady` e' falso e rimanda a /login se l'utente manca. Quando si
  // arriva qui, l'utente collegato c'e'.
  const chiamanteEAdmin = hasRole(utenteCollegato, "ADMIN", "SUPERADMIN");

  // LA VIA D'USCITA, una sola: il «Back» della banda, e la chiusura del
  // modulo dopo un salvataggio riuscito.
  // ⚠️ `location.key === "default"` e' il modo di react-router v6 per dire
  // «prima voce della cronologia»: ci si arriva per link diretto, segnalibro o
  // ricaricamento. In quel caso `navigate(-1)` uscirebbe dall'app. View
  // Profile ripiega su /home; qui si ripiega sulla SCHEDA della stessa
  // persona, che e' il posto da cui questa pagina si apre sempre.
  const tornaIndietro = useCallback(() => {
    if (location.key === "default") {
      navigate(`/profile/${idProfilo}`, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate, idProfilo]);

  // "caricamento" | "pronto" | "errore"
  const [statoProfilo, setStatoProfilo] = useState("caricamento");
  const [utenteDaModificare, setUtenteDaModificare] = useState(null);

  // L'id di cui una risposta e' ancora valida: una lettura partita per una
  // persona non deve disegnarne un'altra, ne' scrivere su un componente
  // smontato.
  const idInCorsoRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Senza titolo non si legge nulla: il documento non si chiede nemmeno.
    if (!idProfilo || !chiamanteEAdmin) {
      setStatoProfilo("errore");
      setUtenteDaModificare(null);
      return undefined;
    }

    setStatoProfilo("caricamento");
    setUtenteDaModificare(null);
    idInCorsoRef.current = idProfilo;

    // La porta unica del documento intero, la stessa da cui la scheda legge
    // (`ricaricaProfilo` in MemberProfileModal): nessuna lettura nuova.
    api
      .get(`/users/${idProfilo}`)
      .then((documento) => {
        if (idInCorsoRef.current !== idProfilo) return;
        setUtenteDaModificare(documento);
        setStatoProfilo("pronto");
      })
      .catch(() => {
        // Un solo esito per ogni rifiuto (403, 404, guasto tecnico): la
        // pagina non deve lasciar capire se quella persona esista.
        if (idInCorsoRef.current !== idProfilo) return;
        setUtenteDaModificare(null);
        setStatoProfilo("errore");
      });

    return () => {
      idInCorsoRef.current = null;
    };
  }, [idProfilo, chiamanteEAdmin]);

  // Se la scheda salvata e' la PROPRIA, l'utente collegato di AuthContext va
  // riscritto, altrimenti resterebbe quello di prima fino a un nuovo login.
  // E' cio' che faceva `propagaAggiornamento` della scheda quando ospitava la
  // modale: si passa dalla stessa sede unica.
  // R9.10: la rilettura si ATTENDE, con un catch proprio, come in
  // UserContextManager — è solo una lettura dopo un salvataggio riuscito: se
  // fallisce lo si annota in console e non si dice nulla a schermo, ma la
  // promessa non resta senza gestione.
  const propagaAggiornamento = useCallback(async () => {
    if (idProfilo && utenteCollegato?.id === idProfilo && refreshUserFromBackend) {
      try {
        await refreshUserFromBackend();
      } catch (err) {
        console.error("User reload failed after save:", err);
      }
    }
  }, [idProfilo, utenteCollegato?.id, refreshUserFromBackend]);

  // IL RIFIUTO, DENTRO IL TELAIO — come View Profile: la pagina porta la barra
  // utente e il «Back», e dice una riga sola. La stessa per il non-admin
  // arrivato per URL, per l'id inesistente e per il guasto: nessuna delle tre
  // lascia capire nulla di piu'.
  if (!chiamanteEAdmin) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        <p className="qpsc-body-text qpsc-body-text--muted">
          This page is not available.
        </p>
      </HomeShell>
    );
  }

  if (statoProfilo !== "pronto" || !utenteDaModificare) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        <p className="qpsc-body-text qpsc-body-text--muted">
          {statoProfilo === "caricamento"
            ? "Loading profile…"
            : "This profile is not available."}
        </p>
      </HomeShell>
    );
  }

  return (
    <HomeShell backTo={tornaIndietro} showSwitch={false}>
      {/* Il modulo. Il telaio lo mette questa pagina.
          ⚠️ `onClose` e' la stessa uscita del «Back», ed e' ora l'UNICA via:
          dalla fase 2 il modulo non ha piu' un «Cancel» proprio (§12.5,
          l'annullamento e' il «Back» della banda), quindi la sola chiamata
          che resta e' la chiusura 800ms dopo un salvataggio riuscito — che,
          invece di scoprire la scheda sotto, ci torna. La scheda si rimonta e
          rilegge da se': non serve passarle il documento fresco, e infatti
          `onSave` qui si occupa della sola cosa che il ritorno non
          produrrebbe da solo. */}
      <EditMemberModal
        user={utenteDaModificare}
        onClose={tornaIndietro}
        onSave={propagaAggiornamento}
      />
    </HomeShell>
  );
};

export default EditMemberPage;
